import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Square from "./Square";
import SquareWithPiece from "./SquareWithPiece";
import config from '../../config';
import parseFEN from './parseFEN';

function Chessboard({
    setClickedSquare,
    amICorrect,
    boardSize = 8,
    highlightRows = false,
    highlightCols = false,
    squaresToHighlight = [],
    displaysPieces = false,
    fen = null,
    displaysChecker = false,
    flipped = false,
    sideToMove = null,
    locked = false,
    onDropMove = null,
}) {
    const letters = 'abcdefghijklmnopqrstuvwxyz'.slice(0, boardSize).split('');
    const board = parseFEN(fen);

    // Pointer-based drag state
    const boardGridRef = useRef(null);
    const [drag, setDrag] = useState(null); // { fromSquare, pieceImg, x, y }

    const startDrag = (fromSquare, pieceImg, e) => {
        if (locked || !pieceImg || !onDropMove) return;
        e.preventDefault();
        setDrag({ fromSquare, pieceImg, x: e.clientX, y: e.clientY });
    };

    const handleOverlayMove = (e) => {
        if (!drag) return;
        setDrag(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
    };

    const handleOverlayUp = (e) => {
        if (!drag) return;
        const rect = boardGridRef.current?.getBoundingClientRect();
        if (rect) {
            const sq = rect.width / boardSize;
            const col = Math.floor((e.clientX - rect.left) / sq);
            const row = Math.floor((e.clientY - rect.top)  / sq);
            if (col >= 0 && col < boardSize && row >= 0 && row < boardSize) {
                const colIdx = flipped ? boardSize - 1 - col : col;
                const rowIdx = flipped ? boardSize - 1 - row : row;
                const toSquare = `${letters[colIdx]}${boardSize - rowIdx}`;
                if (toSquare !== drag.fromSquare && onDropMove) {
                    onDropMove(drag.fromSquare, toSquare);
                }
            }
        }
        setDrag(null);
    };

    const rowIndices = flipped
        ? Array.from({ length: boardSize }, (_, i) => boardSize - 1 - i)
        : Array.from({ length: boardSize }, (_, i) => i);
    const colIndices = flipped
        ? Array.from({ length: boardSize }, (_, i) => boardSize - 1 - i)
        : Array.from({ length: boardSize }, (_, i) => i);

    const squares = [];
    for (const row of rowIndices) {
        for (const col of colIndices) {
            const isBlack = boardSize % 2 === 0 ? (row + col) % 2 === 1 : (row + col) % 2 === 0;
            const position = `${letters[col]}${boardSize - row}`;

            if (displaysPieces) {
                const piece = board[row][col];
                const isWhitePiece = piece && piece === piece.toUpperCase();
                const isActiveSide = sideToMove && piece
                    ? (sideToMove === 'w' ? isWhitePiece : !isWhitePiece)
                    : false;
                const pieceImg = piece
                    ? displaysChecker
                        ? (isWhitePiece ? config.chessPieces['wchecker'] : config.chessPieces['bchecker'])
                        : (isWhitePiece ? config.chessPieces[`w${piece.toLowerCase()}`] : config.chessPieces[`b${piece}`])
                    : null;

                squares.push(
                    <SquareWithPiece
                        key={`${row}-${col}`}
                        isBlack={isBlack}
                        amICorrect={amICorrect}
                        position={position}
                        setClickedSquare={setClickedSquare}
                        boardSize={boardSize}
                        highlight={squaresToHighlight.includes(position)}
                        piece={pieceImg}
                        isActiveSide={isActiveSide}
                        locked={locked}
                        isDraggingFrom={drag?.fromSquare === position}
                        onPieceDragStart={startDrag}
                    />
                );
            } else {
                squares.push(
                    <Square
                        key={`${row}-${col}`}
                        isBlack={isBlack}
                        position={position}
                        setClickedSquare={setClickedSquare}
                        amICorrect={amICorrect}
                        boardSize={boardSize}
                        highlight={squaresToHighlight.includes(position)}
                    />
                );
            }
        }
    }

    const rankLabels = rowIndices.map(row => boardSize - row);
    const fileLabels = colIndices.map(col => letters[col]);

    // Floating piece during pointer drag (rendered into document.body via portal)
    const floatingPiece = drag && boardGridRef.current
        ? ReactDOM.createPortal(
            <img
                src={drag.pieceImg}
                alt=""
                style={{
                    position: 'fixed',
                    left: drag.x - (boardGridRef.current.getBoundingClientRect().width / boardSize) / 2,
                    top:  drag.y - (boardGridRef.current.getBoundingClientRect().width / boardSize) / 2,
                    width:  boardGridRef.current.getBoundingClientRect().width / boardSize,
                    height: boardGridRef.current.getBoundingClientRect().width / boardSize,
                    pointerEvents: 'none',
                    zIndex: 9999,
                    filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.6))',
                    opacity: 0.92,
                }}
            />,
            document.body
        )
        : null;

    return (
        <div style={styles.boardContainer}>
            {/* Full-screen capture overlay while dragging */}
            {drag && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 9998, cursor: 'grabbing' }}
                    onPointerMove={handleOverlayMove}
                    onPointerUp={handleOverlayUp}
                    onPointerLeave={handleOverlayUp}
                />
            )}

            <div style={styles.mainContainer}>
                <div style={styles.leftRanks(boardSize)}>
                    {rankLabels.map(rank => (
                        <div key={rank} style={styles.rankLabel(boardSize, highlightCols)}>{rank}</div>
                    ))}
                </div>
                <div ref={boardGridRef} style={styles.container(boardSize)}>
                    {squares}
                </div>
            </div>
            <div style={styles.bottomFiles(boardSize)}>
                {fileLabels.map(letter => (
                    <div key={letter} style={styles.fileLabel(boardSize, highlightRows)}>{letter}</div>
                ))}
            </div>

            {floatingPiece}
        </div>
    );
}

const styles = {
    boardContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        color: 'white',
    },
    mainContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: (boardSize) => ({
        display: 'flex',
        flexWrap: 'wrap',
        width: `${boardSize * 5}vw`,
        height: `${boardSize * 5}vw`,
        borderRadius: '20px',
        overflow: 'hidden',
        position: 'relative',
    }),
    bottomFiles: (boardSize) => ({
        display: 'flex',
        paddingLeft: '2vmin',
        marginTop: '-2vmin',
        justifyContent: 'center',
        width: '100%',
    }),
    fileLabel: (boardSize, highlightRows) => ({
        width: '5vw',
        textAlign: 'center',
        fontSize: highlightRows ? '2em' : '1.5em',
        fontWeight: 'bold',
        color: highlightRows ? 'green' : 'white',
    }),
    leftRanks: (boardSize) => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        paddingRight: '1vmin',
        paddingTop: '4vmin',
    }),
    rankLabel: (boardSize, highlightCols) => ({
        height: '5vw',
        textAlign: 'center',
        fontSize: highlightCols ? '2em' : '1.5em',
        fontWeight: 'bold',
        color: highlightCols ? 'green' : 'white',
    }),
};

export default Chessboard;
